// src/components/tools/TagSelector.tsx
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { Form, ListGroup } from 'react-bootstrap'
import { useGetTagsQuery, useCreateTagMutation } from '../../services/tagApi'
import { Tag } from '../../types/models/tag'
import { useAppSelector } from '../../store/hooks'
import { selectSelectedProjectId } from '../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../services/projectsApi'
import { generateRandomColor } from '../../utils/helpers'
import ErrorDialog from '../modals/ErrorDialog'

interface TagListSelectorProps {
  onTagSelect: (tag: Tag) => void
  position?: { x: number; y: number }
  onClose?: () => void
}

const TagSelector: React.FC<TagListSelectorProps> = ({ onTagSelect, position, onClose }) => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const [filter, setFilter] = useState('')
  const [filteredTags, setFilteredTags] = useState<Tag[]>([])
  const [createTag] = useCreateTagMutation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string>('')
  const [showError, setShowError] = useState(false)
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const listContainerRef = useRef<HTMLDivElement>(null)
  const storageKey = useMemo(
    () => `av:lastTag:${selectedProjectId || 'proj-unk'}:${project?.datasetid || 'ds-unk'}`,
    [selectedProjectId, project?.datasetid]
  )
  const [lastTagId, setLastTagId] = useState<string | null>(null)

  const {
    data: tags,
    isLoading,
    error: apiError,
  } = useGetTagsQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
    },
    { skip: !selectedProjectId || !project?.datasetid }
  )

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) setLastTagId(saved)
    } catch {
      // ignore storage errors
    }
  }, [storageKey])

  const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value)
  }, [])

  const handleTagSelect = useCallback(
    async (tag: Tag) => {
      try {
        localStorage.setItem(storageKey, tag.id)
        setLastTagId(tag.id)
      } catch {
        // ignore storage errors
      }
      onTagSelect(tag)
      if (onClose) {
        onClose()
      }
    },
    [onTagSelect, onClose, storageKey]
  )

  useEffect(() => {
    const filtered =
      tags?.tags
        ?.filter((tag: Tag) => tag.name.toLowerCase().includes(filter.toLowerCase()))
        .sort((a: Tag, b: Tag) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
        .filter((t: Tag) => t.id !== lastTagId) || []
    setFilteredTags(filtered)
    setActiveIndex(0)
  }, [filter, tags, lastTagId])

  useEffect(() => {
    if (!listContainerRef.current) return
    const listItems = listContainerRef.current.querySelectorAll<HTMLElement>('[data-tag-index]')
    const activeEl = Array.from(listItems).find(
      (el) => Number(el.getAttribute('data-tag-index')) === activeIndex
    )
    if (activeEl) {
      activeEl.scrollIntoView({ block: 'nearest' })
    }
  }, [activeIndex, filteredTags.length])

  const handleContainerKeyDownCapture = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    const keysToStop = new Set([
      'Delete',
      'Tab',
      'Escape',
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
      '+',
      '=',
      '-',
      '_',
    ])
    if (keysToStop.has(e.key)) {
      e.stopPropagation()
    }
  }, [])

  const handleContainerWheelCapture = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    e.stopPropagation()
  }, [])

  const handleKeyPress = useCallback(
    async (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((prev) => Math.min(prev + 1, Math.max(0, filteredTags.length - 1)))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((prev) => Math.max(0, prev - 1))
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        const byIndex = filteredTags[activeIndex]
        if (byIndex) {
          await handleTagSelect(byIndex)
          return
        }
        if (filter.trim() !== '') {
          const existingTag = filteredTags.find(
            (tag) => tag.name.toLowerCase() === filter.toLowerCase()
          )
          if (existingTag) {
            await handleTagSelect(existingTag)
          } else {
            try {
              const newTag = await createTag({
                name: filter,
                color: generateRandomColor().number,
                dataset_id: project?.datasetid || '',
                project_id: selectedProjectId || '',
              }).unwrap()
              await handleTagSelect(newTag)
            } catch (err: unknown) {
              let message = 'Failed to create tag'
              if (typeof err === 'object' && err !== null) {
                const e = err as { data?: { message?: string }; message?: string }
                message = e.data?.message || e.message || message
              }
              setError(message)
              setShowError(true)
            }
          }
        }
      }
    },
    [filter, filteredTags, activeIndex, handleTagSelect, createTag, project, selectedProjectId]
  )

  if (isLoading) return <div>Loading tags...</div>
  if (apiError) return <div>Error loading tags</div>

  const selectorContent = (
    <>
      <Form.Control
        ref={inputRef}
        type="text"
        placeholder="Filter or create new tag"
        value={filter}
        onChange={handleFilterChange}
        onKeyDown={handleKeyPress}
        className="bg-dark text-white border-secondary"
      />
      {lastTagId && (
        <div className="mt-2">
          <div className="text-secondary small mb-1">Last used</div>
          <div className="d-flex">
            {(() => {
              const tag = tags?.tags?.find((t: Tag) => t.id === lastTagId)
              if (!tag) return null
              return (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-light border-secondary text-white d-flex align-items-center gap-2"
                  onClick={() => handleTagSelect(tag)}
                >
                  <span
                    className="d-inline-block rounded-circle"
                    style={{
                      width: 10,
                      height: 10,
                      backgroundColor: `#${(tag.color >>> 0).toString(16).padStart(6, '0')}`,
                    }}
                  />
                  <span className="text-truncate">{tag.name}</span>
                </button>
              )
            })()}
          </div>
          <hr className="border-secondary my-2" />
        </div>
      )}
      <div className="mt-2 overflow-auto" style={{ maxHeight: 240 }} ref={listContainerRef}>
        <ListGroup variant="flush">
          {filteredTags.map((tag: Tag, idx: number) => (
            <ListGroup.Item
              key={tag.id}
              action
              onClick={() => handleTagSelect(tag)}
              onMouseEnter={() => setActiveIndex(idx)}
              className="bg-dark text-white border-secondary d-flex align-items-center gap-2 py-1"
              active={idx === activeIndex}
              data-tag-index={idx}
              style={{ cursor: 'pointer' }}
            >
              <span
                className="d-inline-block rounded-circle"
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: `#${(tag.color >>> 0).toString(16).padStart(6, '0')}`,
                }}
              />
              <span className="text-truncate">{tag.name}</span>
            </ListGroup.Item>
          ))}
        </ListGroup>
      </div>
    </>
  )

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    ...(position && {
      position: 'absolute',
      left: `${position.x}px`,
      top: `${position.y}px`,
      zIndex: 9999,
    }),
    minWidth: 250,
  }

  return (
    <div
      style={containerStyle}
      className="bg-dark text-white border border-secondary rounded shadow p-2"
      role="dialog"
      aria-modal="true"
      onKeyDownCapture={handleContainerKeyDownCapture}
      onWheelCapture={handleContainerWheelCapture}
    >
      {onClose && (
        <button
          type="button"
          className="btn-close btn-close-white position-absolute top-0 end-0 m-2"
          aria-label="Close"
          onClick={onClose}
        />
      )}
      {selectorContent}

      <ErrorDialog
        show={showError}
        onHide={() => setShowError(false)}
        message={error}
        title="Tag Creation Error"
      />
    </div>
  )
}

export default TagSelector
